import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';

import { ProviderCard } from '@/components/discovery/provider-card';
import { CitySelectorSheet } from '@/components/discovery/city-selector-sheet';
import { ThemedText } from '@/components/themed-text';
import { PressableCard } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { trackDiscoveryEvent, useDiscoveryHighlights } from '@/hooks/use-discovery';
import { useRegionStore } from '@/store/regionStore';
import { cn } from '@/utils/cn';
import { ProviderCard as ProviderCardType } from '@/types/discovery';

const CARD_WIDTH = 220;

/**
 * Carrossel "prestadores perto de você" (B1). É a única superfície de descoberta
 * na home: sem ela, o prestador só é achado por quem já tem o link dele.
 */
export function ProviderCarousel({ className }: { className?: string }) {
  const { region, setRegion } = useRegionStore();
  const [cityPickerOpen, setCityPickerOpen] = useState(false);

  const { data, isLoading } = useDiscoveryHighlights(region);
  const trackedRef = useRef<string>('');

  const cards = useMemo(() => data ?? [], [data]);
  const regionLabel = region.city ? `${region.city} · ${region.uf}` : 'Todo o Brasil';

  // Uma impressão por render de lista, não por card visível: barato e suficiente
  // para medir alcance sem instrumentar viewport.
  useEffect(() => {
    if (cards.length === 0) return;

    const signature = cards.map((card) => card.providerId).join(',');
    if (trackedRef.current === signature) return;
    trackedRef.current = signature;

    cards.forEach((card, index) => {
      trackDiscoveryEvent({
        providerId: card.providerId,
        type: 'IMPRESSION',
        surface: 'HOME_CAROUSEL',
        sponsorshipId: card.sponsorshipId,
        region,
        position: index + 1,
      });
    });
  }, [cards, region]);

  // A navegação é do `<Link>` do card; aqui fica só a métrica do clique.
  const trackProviderClick = (card: ProviderCardType, index: number) => {
    trackDiscoveryEvent({
      providerId: card.providerId,
      type: 'CLICK',
      surface: 'HOME_CAROUSEL',
      sponsorshipId: card.sponsorshipId,
      region,
      position: index + 1,
    });
  };

  if (!isLoading && cards.length === 0) return null;

  return (
    <View className={cn('w-full max-w-2xl gap-3', className)}>
      <View className="flex-row items-end justify-between">
        <View className="flex-1 gap-0.5">
          <ThemedText className="font-bold text-lg text-foreground">
            Prestadores perto de você
          </ThemedText>

          <Pressable
            onPress={() => setCityPickerOpen(true)}
            className="flex-row items-center gap-1"
            hitSlop={8}
          >
            <Ionicons name="location" size={13} color="#FD5001" />
            <ThemedText className="font-semibold text-xs text-primary">{regionLabel}</ThemedText>
            <Ionicons name="chevron-down" size={13} color="#FD5001" />
          </Pressable>
        </View>

        <Pressable onPress={() => router.push('/buscar' as never)} hitSlop={8}>
          <ThemedText className="font-semibold text-sm text-primary">Ver todos</ThemedText>
        </Pressable>
      </View>

      <ScrollView
        keyboardShouldPersistTaps="handled"
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ gap: 12, paddingRight: 4 }}
      >
        {isLoading
          ? [0, 1, 2].map((key) => (
              <Skeleton key={key} className="h-56 rounded-2xl" style={{ width: CARD_WIDTH }} />
            ))
          : cards.map((card, index) => (
              <View key={card.providerId} style={{ width: CARD_WIDTH }}>
                <ProviderCard
                  card={card}
                  href={`/@${card.slug}`}
                  onPress={() => trackProviderClick(card, index)}
                />
              </View>
            ))}

        {!isLoading && cards.length > 0 ? (
          <PressableCard
            onPress={() => router.push('/buscar' as never)}
            className="items-center justify-center gap-2 p-4"
            style={{ width: 130 }}
          >
            <View className="h-11 w-11 items-center justify-center rounded-2xl bg-primary-50 dark:bg-primary">
              <Ionicons className="text-primary dark:text-white" name="search" size={20} />
            </View>
            <ThemedText className="text-center font-semibold text-xs text-foreground">
              Ver todos
            </ThemedText>
          </PressableCard>
        ) : null}
      </ScrollView>

      <CitySelectorSheet
        open={cityPickerOpen}
        region={region}
        onClose={() => setCityPickerOpen(false)}
        onSelect={setRegion}
      />
    </View>
  );
}
